import z from 'zod'
import {
  nullSafeOptionalBoolean,
  nullSafeOptionalFirestoreTimestamp,
  nullSafeOptionalString,
  nullSafeString,
} from '../shared/schema'
import { promptVersionPromptFields } from '../../types'

export const schema = z.object({
  updatedAt: nullSafeOptionalFirestoreTimestamp(),
  minScore: z.coerce.number(),
  maxScore: z.coerce.number(),
  description: nullSafeString(''),
  title: nullSafeString(''),
  scoreDescriptions: z.preprocess(
    (val) => val ?? [],
    z.array(
      z.object({
        score: z.coerce.number(),
        title: nullSafeString(''),
        explanation: nullSafeString(''),
        prompt: nullSafeString(''),
        color: nullSafeString(''),
        textColor: nullSafeOptionalString(),
        meaning: nullSafeString(''),
      })
    )
  ),
  // Prefillable rubric presets for this type. A type with variants can stamp a
  // whole set of rubrics onto a slide deck; `variantKey` is the stable identity
  // used to avoid creating duplicates.
  variants: z.preprocess(
    (val) => val ?? [],
    z.array(
      z.object({
        variantKey: nullSafeString(''),
        order: z.coerce.number(),
        title: nullSafeString(''),
        description: nullSafeString(''),
        rubricPrompt: nullSafeString(''),
      })
    )
  ),
  scoreGrouping: z
    .record(
      z.string(),
      z.object({
        color: z.string(),
        scores: z.array(z.coerce.number()),
      })
    )
    .nullish()
    .optional(),
  requiredPrompts: z.array(z.enum(promptVersionPromptFields)).default([]),
  optionalPrompts: z.array(z.enum(promptVersionPromptFields)).default([]),
  promptSchemas: z
    .record(z.enum(promptVersionPromptFields), z.string())
    .default({}),
  trigger: nullSafeOptionalString(),
  showTestingTab: nullSafeOptionalBoolean(false),
  excludeSoloExperiences: nullSafeOptionalBoolean(false),
  // Whether a rubric of this type requires its per-instance directive
  // (`rubricPrompt`, shown as "Additional Prompt"). Absent = fall back to the
  // isPromptRequired() default in the web app.
  requiresRubricPrompt: nullSafeOptionalBoolean(),
})

export const empty = Object.freeze(
  schema.parse({
    updatedAt: null,
    minScore: 0,
    maxScore: 0,
    description: '',
    title: '',
    scoreDescriptions: [],
    variants: [],
    requiredPrompts: [],
    optionalPrompts: [],
  })
)

export type FirestorePromptRubricType = z.infer<typeof schema>
